
export const TRACE_FORMAT = 'bug-trace/1';
export const TRACE_MAX = 2000;
export const TRACE_SAMPLE_MS = { scroll: 250, drag: 250, move: 500 } as const;
export type TraceSample = keyof typeof TRACE_SAMPLE_MS;

/** `widget` — a thing the report panel did (a frame started, the shield switched, a shot removed), not a pointer event. */
export type TraceType = 'click' | 'move' | 'scroll' | 'hover' | 'nav' | 'widget';

export interface TraceEvent {
  t: number;
  type: TraceType;
  /** Happened in the report widget (panel, pill, frame): recorded, but not the person's step on the page (reports 105–107). */
  own?: 'widget';
  x?: number;
  y?: number;
  vw?: number;
  vh?: number;
  sx?: number;
  sy?: number;
  target?: string;
  path?: string;
  b?: 1;
}

export interface TraceFile {
  format: typeof TRACE_FORMAT;
  startedAt: string;
  durationMs: number;
  viewport: { w: number; h: number };
  dropped: number;
  events: TraceEvent[];
}

const sampleOf = (event: Omit<TraceEvent, 't'>): TraceSample | null =>
  event.type === 'scroll' ? 'scroll' : event.type === 'move' ? (event.b ? 'drag' : 'move') : null;

export class TraceBuffer {
  readonly events: TraceEvent[] = [];
  dropped = 0;
  private pushed = 0;
  private lastAt = new Map<TraceSample, number>();

  constructor(
    readonly startedAt: number,
    private readonly max = TRACE_MAX,
  ) {}

  due(sample: TraceSample, now: number): boolean {
    const last = this.lastAt.get(sample);
    if (last !== undefined && now - last < TRACE_SAMPLE_MS[sample]) return false;
    this.lastAt.set(sample, now);
    return true;
  }

  add(now: number, event: Omit<TraceEvent, 't'>): boolean {
    const sample = sampleOf(event);
    if (sample && !this.due(sample, now)) return false;
    this.push(now, event);
    return true;
  }

  push(now: number, event: Omit<TraceEvent, 't'>): void {
    this.events.push({ t: Math.max(0, Math.round(now - this.startedAt)), ...event });
    this.pushed += 1;
    if (this.events.length > this.max) {
      const extra = this.events.length - this.max;
      this.events.splice(0, extra);
      this.dropped += extra;
    }
  }

  /**
   * Everything kept, re-based to the first event; `epochNow` — `Date.now()` at `now`. The quiet trace of a report: what
   * happened since the previous report (`dropSent`), not the whole page life.
   */
  tail(now: number, epochNow: number, viewport: { w: number; h: number }, clean: (text: string) => string = (text) => text): TraceFile | null {
    if (this.events.length === 0) return null;
    const t0 = this.events[0].t;
    return {
      format: TRACE_FORMAT,
      startedAt: new Date(epochNow - (now - (this.startedAt + t0))).toISOString(),
      durationMs: Math.max(0, Math.round(now - this.startedAt - t0)),
      viewport,
      dropped: this.dropped,
      events: this.events.map((event) => ({
        ...event,
        t: event.t - t0,
        ...(event.target !== undefined ? { target: clean(event.target) } : {}),
        ...(event.path !== undefined ? { path: clean(event.path) } : {}),
      })),
    };
  }

  /** How many events were ever pushed: `dropSent(mark)` forgets those up to it. */
  mark(): number {
    return this.pushed;
  }

  /** Forgets the events pushed up to `mark` (they went with a sent report); later ones stay. */
  dropSent(mark: number): void {
    const gone = this.pushed - this.events.length;
    this.events.splice(0, Math.max(0, Math.min(this.events.length, mark - gone)));
    this.dropped = 0;
  }

  toFile(now: number, startedAtIso: string, viewport: { w: number; h: number }, clean: (text: string) => string = (text) => text): TraceFile {
    return {
      format: TRACE_FORMAT,
      startedAt: startedAtIso,
      durationMs: Math.max(0, Math.round(now - this.startedAt)),
      viewport,
      dropped: this.dropped,
      events: this.events.map((event) => ({
        ...event,
        ...(event.target !== undefined ? { target: clean(event.target) } : {}),
        ...(event.path !== undefined ? { path: clean(event.path) } : {}),
      })),
    };
  }
}

export function traceLabel(events: number, durationMs: number): string {
  const sec = Math.floor(durationMs / 1000);
  return `${events} · ${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`;
}
